/**
 * Which frames are selected, mirrored across desktop windows. The canvas
 * store's selection actions write these ids here and only paint the nodes
 * (`selected`, z-order); the wall and the panels read this store.
 */
import { create } from "zustand";
import { mirroredStore } from "@/lib/mirrored-store";

export interface SelectionStoreState {
  /** The primary selection (drives the right panel). */
  selectedFrameId: string | null;
  /** Frames picked with ⌘/Ctrl-click; empty without a multi-selection. */
  multiSelectedFrameIds: string[];
  /** Increments on every selection, so effects fire on a re-select too. */
  selectionSeq: number;
  select: (frameId: string | null) => void;
  toggleMulti: (frameId: string) => void;
  setMulti: (frameIds: string[]) => void;
  clearMulti: () => void;
  /** Drop selected ids `keep` rejects, such as frames that left the space. */
  retain: (keep: (frameId: string) => boolean) => void;
}

export const useSelectionStore = create<SelectionStoreState>()(
  mirroredStore("selection", (set) => ({
    selectedFrameId: null,
    multiSelectedFrameIds: [],
    selectionSeq: 0,

    select: (frameId) =>
      set((state) => ({
        selectedFrameId: frameId,
        multiSelectedFrameIds: [],
        selectionSeq: state.selectionSeq + 1,
      })),

    toggleMulti: (frameId) =>
      set((state) => {
        // A single selection joins the multi-selection it starts.
        const ids =
          state.multiSelectedFrameIds.length === 0 &&
          state.selectedFrameId &&
          state.selectedFrameId !== frameId
            ? [state.selectedFrameId]
            : state.multiSelectedFrameIds;
        const next = ids.includes(frameId)
          ? ids.filter((id) => id !== frameId)
          : [...ids, frameId];
        return {
          multiSelectedFrameIds: next,
          selectedFrameId: next[next.length - 1] ?? null,
          selectionSeq: state.selectionSeq + 1,
        };
      }),

    setMulti: (frameIds) =>
      set((state) => {
        if (frameIds.length === 0) return state;
        const next = [...new Set(frameIds)];
        return {
          multiSelectedFrameIds: next,
          selectedFrameId: next[next.length - 1]!,
          selectionSeq: state.selectionSeq + 1,
        };
      }),

    clearMulti: () =>
      set((state) =>
        state.multiSelectedFrameIds.length === 0
          ? state
          : { multiSelectedFrameIds: [] },
      ),

    retain: (keep) =>
      set((state) => {
        const multiSelectedFrameIds = state.multiSelectedFrameIds.filter(keep);
        const dropsSelected =
          state.selectedFrameId !== null && !keep(state.selectedFrameId);
        if (
          !dropsSelected &&
          multiSelectedFrameIds.length === state.multiSelectedFrameIds.length
        ) {
          return state;
        }
        return {
          multiSelectedFrameIds,
          ...(dropsSelected && { selectedFrameId: null }),
        };
      }),
  })),
);
